/**
 * useLongPressRangeSelect — Google Photos–style touch selection.
 *
 * - Long-press (LONG_PRESS_MS) on a photo cell enters "drag mode" and seeds
 *   the anchor at that photo.
 * - While the finger moves, the selection is computed as
 *     baseSelection ∪ range(anchor, currentPhotoUnderFinger)
 *   and synced via onSelectMany / onDeselectMany.
 * - When the finger approaches the top or bottom edge of the scroller, the
 *   scroller auto-scrolls; speed grows as the finger gets closer to the edge.
 * - Release: exits drag mode; already-selected photos stay selected.
 *
 * Uses Pointer Events (touch only — pointerType === 'touch'). Mouse/desktop
 * behavior is untouched. The hook does NOT preempt native scroll until the
 * long-press fires; before that, normal scrolling is intact. Once drag mode
 * starts, the scroller's touch-action is forced to 'none', the pointer is
 * captured on the container, and pointermove preventDefault stops any
 * residual native scroll. State is fully restored on release.
 */

import { useEffect, useRef } from 'react';

const LONG_PRESS_MS = 400;
const MOVE_CANCEL_THRESHOLD_PX = 10;
// Edge zones are wide on mobile so the user has room to keep their finger
// inside the active band without needing to dangle off the screen.
const EDGE_ZONE_PX = 140;
const MAX_SCROLL_SPEED_PX = 22;

export interface LongPressRangeSelectOptions {
  containerRef: React.RefObject<HTMLElement>;
  getScroller: () => HTMLElement | null;
  photoCount: number;
  indexOfPhotoId: (photoId: string) => number;
  photoIdAt: (index: number) => string | undefined;
  getCurrentSelection: () => Set<string>;
  onSelectMany: (ids: string[]) => void;
  onDeselectMany: (ids: string[]) => void;
  onDragModeChange?: (active: boolean) => void;
  disabled?: boolean;
}

export function useLongPressRangeSelect(options: LongPressRangeSelectOptions) {
  const {
    containerRef,
    getScroller,
    photoCount,
    indexOfPhotoId,
    photoIdAt,
    getCurrentSelection,
    onSelectMany,
    onDeselectMany,
    onDragModeChange,
    disabled,
  } = options;

  const cbRef = useRef({
    indexOfPhotoId,
    photoIdAt,
    getCurrentSelection,
    onSelectMany,
    onDeselectMany,
    onDragModeChange,
    getScroller,
    photoCount,
  });
  cbRef.current = {
    indexOfPhotoId,
    photoIdAt,
    getCurrentSelection,
    onSelectMany,
    onDeselectMany,
    onDragModeChange,
    getScroller,
    photoCount,
  };

  useEffect(() => {
    if (disabled) return;
    const el = containerRef.current;
    if (!el) return;

    let longPressTimer: number | null = null;
    let activePointerId: number | null = null;
    let startX = 0;
    let startY = 0;
    let lastTouchX = 0;
    let lastTouchY = 0;
    let startPhotoId: string | null = null;
    let anchorIndex: number | null = null;
    let dragActive = false;
    let baseSelection: Set<string> = new Set();
    let dragAdded: Set<string> = new Set();
    let scroller: HTMLElement | null = null;
    let autoScrollRafId: number | null = null;
    let autoScrollSpeed = 0;
    // Watchdog: while dragging we expect touchmove/pointermove every ~16ms.
    // If we go silent for too long the browser has likely lost the pointer
    // (after a swallowed pointercancel, the OS may stop emitting events for
    // that touch). We then assume the finger was lifted and end the drag.
    let watchdogTimer: number | null = null;
    // Saved styles to restore on endDrag (we override these during drag to
    // suppress native scroll / pull-to-refresh on touch).
    let savedScrollerTouchAction: string | null = null;
    let savedBodyOverscrollBehavior: string | null = null;
    let savedHtmlTouchAction: string | null = null;
    let savedBodyTouchAction: string | null = null;
    let pointerCaptured = false;
    // Track ancestor scrollables we froze during drag so we can restore them.
    type FrozenAncestor = { el: HTMLElement; overflow: string; overflowY: string };
    let frozenAncestors: FrozenAncestor[] = [];

    // Walk up from the container and freeze every ancestor that owns an
    // independent vertical scroller (overflow auto/scroll), EXCLUDING the
    // root <html>/<body>. Touching the root's overflow on mobile (especially
    // Chrome Android) toggles the URL bar and triggers a layout shift, which
    // in turn causes the browser to emit pointercancel — breaking the drag.
    // We rely on touch-action:none + preventDefault on touchmove to suppress
    // the root scroll instead.
    const freezeAncestorScrollables = (start: HTMLElement | null) => {
      const frozen: FrozenAncestor[] = [];
      let node: HTMLElement | null = start?.parentElement ?? null;
      const body = document.body;
      while (node && node !== body) {
        const cs = getComputedStyle(node);
        const oy = cs.overflowY;
        const ox = cs.overflow;
        if (oy === 'auto' || oy === 'scroll' || ox === 'auto' || ox === 'scroll') {
          frozen.push({
            el: node,
            overflow: node.style.overflow,
            overflowY: node.style.overflowY,
          });
          node.style.overflow = 'hidden';
          node.style.overflowY = 'hidden';
        }
        node = node.parentElement;
      }
      frozenAncestors = frozen;
    };

    const restoreAncestorScrollables = () => {
      for (const f of frozenAncestors) {
        f.el.style.overflow = f.overflow;
        f.el.style.overflowY = f.overflowY;
      }
      frozenAncestors = [];
    };

    const findPhotoCell = (x: number, y: number): HTMLElement | null => {
      let clampedY = y;
      if (scroller) {
        const rect = scroller.getBoundingClientRect();
        if (clampedY < rect.top + 4) clampedY = rect.top + 4;
        else if (clampedY > rect.bottom - 4) clampedY = rect.bottom - 4;
      }
      const offsets = [0, 12, -12, 24, -24, 40];
      for (const dy of offsets) {
        const node = document.elementFromPoint(x, clampedY + dy);
        if (!node) continue;
        const hit = (node as HTMLElement).closest('[data-photo-id]') as HTMLElement | null;
        if (hit) return hit;
      }
      return null;
    };

    const clearLongPressTimer = () => {
      if (longPressTimer !== null) {
        window.clearTimeout(longPressTimer);
        longPressTimer = null;
      }
    };

    const stopAutoScroll = () => {
      if (autoScrollRafId !== null) {
        cancelAnimationFrame(autoScrollRafId);
        autoScrollRafId = null;
      }
      autoScrollSpeed = 0;
    };

    const stopWatchdog = () => {
      if (watchdogTimer !== null) {
        window.clearInterval(watchdogTimer);
        watchdogTimer = null;
      }
    };

    const startWatchdog = () => {
      stopWatchdog();
      // The only check we trust: are there any fingers on the screen?
      // touchesActive is maintained by touchstart/end at the document level,
      // and is the only reliable signal across browsers. We do NOT use a
      // time-based timeout because during auto-scroll near the screen edge,
      // mobile browsers can stop emitting touchmove events for seconds even
      // while the finger is still pressed.
      watchdogTimer = window.setInterval(() => {
        if (!dragActive) {
          stopWatchdog();
          return;
        }
        if (touchesActive === 0) {
          endDrag();
        }
      }, 100);
    };

    const runAutoScrollFrame = () => {
      if (!dragActive) {
        autoScrollRafId = null;
        return;
      }
      // Recompute speed every frame from the last known finger position,
      // so the scroll keeps moving even if pointermove events stop arriving
      // (which happens on mobile when the finger sits near/off the edge).
      updateAutoScrollForTouchY(lastTouchY);
      if (autoScrollSpeed !== 0) {
        // Try the virtuoso scroller first; if it cannot move (at top/bottom),
        // fall back to scrolling the page (window). On mobile the page itself
        // often owns the scroll, not the inner scroller.
        let consumed = false;
        if (scroller) {
          const before = scroller.scrollTop;
          scroller.scrollTop = before + autoScrollSpeed;
          if (scroller.scrollTop !== before) consumed = true;
        }
        if (!consumed) {
          window.scrollBy(0, autoScrollSpeed);
        }
        updateRangeFromPoint(lastTouchX, lastTouchY);
      }
      autoScrollRafId = requestAnimationFrame(runAutoScrollFrame);
    };

    const updateAutoScrollForTouchY = (clientY: number) => {
      // Compute edge zones against the visible viewport (not the scroller
      // bounds) so the auto-scroll always triggers at the physical edges of
      // the screen on mobile, regardless of where the virtuoso element sits.
      const viewportH = window.visualViewport?.height ?? window.innerHeight;
      // Clamp clientY into the viewport: when the finger goes off the bottom
      // edge of the screen, browsers can report clientY beyond viewportH or
      // freeze it; treat that as "at the edge" so the scroll keeps going.
      const cy = Math.max(0, Math.min(viewportH, clientY));
      const distFromTop = cy;
      const distFromBottom = viewportH - cy;

      let speed = 0;
      if (distFromTop < EDGE_ZONE_PX) {
        const intensity = 1 - Math.max(0, distFromTop) / EDGE_ZONE_PX;
        speed = -Math.round(intensity * MAX_SCROLL_SPEED_PX);
      } else if (distFromBottom < EDGE_ZONE_PX) {
        const intensity = 1 - Math.max(0, distFromBottom) / EDGE_ZONE_PX;
        speed = Math.round(intensity * MAX_SCROLL_SPEED_PX);
      }
      autoScrollSpeed = speed;

      if (speed !== 0 && autoScrollRafId === null) {
        autoScrollRafId = requestAnimationFrame(runAutoScrollFrame);
      }
    };

    const computeRange = (a: number, b: number): number[] => {
      const lo = Math.min(a, b);
      const hi = Math.max(a, b);
      const out: number[] = [];
      for (let i = lo; i <= hi; i++) out.push(i);
      return out;
    };

    const updateRangeFromPoint = (clientX: number, clientY: number) => {
      if (anchorIndex === null) return;
      const cell = findPhotoCell(clientX, clientY);
      if (!cell) return;
      const pid = cell.getAttribute('data-photo-id');
      if (!pid) return;
      const idx = cbRef.current.indexOfPhotoId(pid);
      if (idx < 0) return;

      const rangeIndices = computeRange(anchorIndex, idx);
      const nextDragAdded = new Set<string>();
      for (const i of rangeIndices) {
        const id = cbRef.current.photoIdAt(i);
        if (id) nextDragAdded.add(id);
      }

      const toAdd: string[] = [];
      nextDragAdded.forEach((id) => {
        if (!dragAdded.has(id) && !baseSelection.has(id)) toAdd.push(id);
      });
      const toRemove: string[] = [];
      dragAdded.forEach((id) => {
        if (!nextDragAdded.has(id)) toRemove.push(id);
      });

      if (toAdd.length > 0) cbRef.current.onSelectMany(toAdd);
      if (toRemove.length > 0) cbRef.current.onDeselectMany(toRemove);

      dragAdded = nextDragAdded;
    };

    const startDrag = () => {
      if (startPhotoId === null) return;
      const idx = cbRef.current.indexOfPhotoId(startPhotoId);
      if (idx < 0) return;
      anchorIndex = idx;
      baseSelection = new Set(cbRef.current.getCurrentSelection());
      dragAdded = new Set();
      dragActive = true;
      scroller = cbRef.current.getScroller();
      // Force scroller to ignore touch panning/zoom; save previous value.
      if (scroller) {
        savedScrollerTouchAction = scroller.style.touchAction || '';
        scroller.style.touchAction = 'none';
      }
      // Also lock <html> and <body> — on mobile the page itself often scrolls,
      // not the inner virtuoso scroller, so locking only the scroller is not
      // enough to stop native pan.
      savedHtmlTouchAction = document.documentElement.style.touchAction || '';
      document.documentElement.style.touchAction = 'none';
      savedBodyTouchAction = document.body.style.touchAction || '';
      document.body.style.touchAction = 'none';
      // Disable pull-to-refresh and overscroll bounce while dragging.
      savedBodyOverscrollBehavior = document.body.style.overscrollBehavior || '';
      document.body.style.overscrollBehavior = 'none';
      // Freeze every ancestor scrollable so only the virtuoso scroller can move
      // (via our auto-scroll). Otherwise on mobile the page itself competes
      // with the drag.
      freezeAncestorScrollables(scroller ?? el);
      // Capture pointer on the container so we keep receiving move events
      // even if the finger leaves the original element bounds.
      if (activePointerId !== null) {
        try {
          el.setPointerCapture(activePointerId);
          pointerCaptured = true;
        } catch {
          pointerCaptured = false;
        }
      }
      cbRef.current.onDragModeChange?.(true);
      if (!baseSelection.has(startPhotoId)) {
        dragAdded.add(startPhotoId);
        cbRef.current.onSelectMany([startPhotoId]);
      }
      startWatchdog();
    };

    const endDrag = () => {
      if (dragActive) {
        dragActive = false;
        cbRef.current.onDragModeChange?.(false);
      }
      stopAutoScroll();
      stopWatchdog();
      // Restore scroller touch-action.
      if (scroller && savedScrollerTouchAction !== null) {
        scroller.style.touchAction = savedScrollerTouchAction;
      }
      savedScrollerTouchAction = null;
      // Restore <html> and <body> touch-action.
      if (savedHtmlTouchAction !== null) {
        document.documentElement.style.touchAction = savedHtmlTouchAction;
      }
      savedHtmlTouchAction = null;
      if (savedBodyTouchAction !== null) {
        document.body.style.touchAction = savedBodyTouchAction;
      }
      savedBodyTouchAction = null;
      // Restore body overscrollBehavior.
      if (savedBodyOverscrollBehavior !== null) {
        document.body.style.overscrollBehavior = savedBodyOverscrollBehavior;
      }
      savedBodyOverscrollBehavior = null;
      // Unfreeze any ancestor scrollables we froze.
      restoreAncestorScrollables();
      // Release pointer capture if we took it.
      if (pointerCaptured && activePointerId !== null) {
        try { el.releasePointerCapture(activePointerId); } catch { /* ignore */ }
      }
      pointerCaptured = false;
      anchorIndex = null;
      startPhotoId = null;
      activePointerId = null;
      dragAdded = new Set();
      baseSelection = new Set();
      scroller = null;
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      if (activePointerId !== null) return;

      const cell = findPhotoCell(e.clientX, e.clientY);
      if (!cell) return;

      const pid = cell.getAttribute('data-photo-id');
      if (!pid) return;

      activePointerId = e.pointerId;
      startX = e.clientX;
      startY = e.clientY;
      lastTouchX = e.clientX;
      lastTouchY = e.clientY;
      startPhotoId = pid;

      clearLongPressTimer();
      longPressTimer = window.setTimeout(() => {
        longPressTimer = null;
        if ('vibrate' in navigator) {
          try { navigator.vibrate?.(15); } catch { /* ignore */ }
        }
        startDrag();
      }, LONG_PRESS_MS);
    };

    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      if (e.pointerId !== activePointerId) return;

      lastTouchX = e.clientX;
      lastTouchY = e.clientY;

      if (!dragActive) {
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        if (dx * dx + dy * dy > MOVE_CANCEL_THRESHOLD_PX * MOVE_CANCEL_THRESHOLD_PX) {
          clearLongPressTimer();
          activePointerId = null;
          startPhotoId = null;
        }
        return;
      }

      // Drag mode active: stop any native scroll that may still try to fire.
      if (e.cancelable) e.preventDefault();
      updateRangeFromPoint(e.clientX, e.clientY);
      updateAutoScrollForTouchY(e.clientY);
    };

    const onPointerUp = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      // If a drag is in progress, accept any touch pointerup — the browser
      // may issue the release with a different pointerId after the original
      // pointer's lifecycle was interrupted.
      if (e.pointerId !== activePointerId && !dragActive) return;
      clearLongPressTimer();
      endDrag();
    };

    const onPointerCancel = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      if (e.pointerId !== activePointerId) return;
      clearLongPressTimer();
      endDrag();
    };

    const onContextMenu = (evt: Event) => {
      const t = evt.target as HTMLElement | null;
      if (t && t.closest('[data-photo-id]')) evt.preventDefault();
    };
    const onDragStart = (evt: Event) => {
      const t = evt.target as HTMLElement | null;
      if (t && t.closest('[data-photo-id]')) evt.preventDefault();
    };
    // Mobile browsers (especially iOS Safari) ignore preventDefault() on
    // pointermove for scroll suppression. The reliable mechanism is a
    // non-passive touchmove listener at the document level. Active only
    // while drag mode is on, so normal scrolling is untouched.
    // We also use this event to refresh lastTouchX/Y, because some mobile
    // browsers throttle or stop emitting pointermove when the finger sits
    // close to the screen edge.
    const onDocTouchMove = (evt: TouchEvent) => {
      if (!dragActive) return;
      if (evt.cancelable) evt.preventDefault();
      const t = evt.touches[0];
      if (t) {
        lastTouchX = t.clientX;
        lastTouchY = t.clientY;
        updateAutoScrollForTouchY(t.clientY);
      }
    };
    // Track how many touches are currently on the screen, document-wide.
    // This is the most reliable signal that "the finger is gone" — when
    // touchesActive drops to 0 during a drag, no finger is on the screen,
    // regardless of what pointer-events report.
    let touchesActive = 0;
    const onDocTouchStartTracker = (evt: TouchEvent) => {
      touchesActive = evt.touches.length;
    };
    // Safety net: if pointerup is never delivered (which can happen on some
    // mobile browsers when the pointer lifecycle is interrupted), touchend
    // at the document level still fires when the finger truly lifts.
    const onDocTouchEnd = (evt: TouchEvent) => {
      touchesActive = evt.touches.length;
      if (!dragActive) return;
      // Only end the drag when no fingers remain on the screen.
      if (touchesActive > 0) return;
      clearLongPressTimer();
      endDrag();
    };

    el.addEventListener('pointerdown', onPointerDown);
    // pointermove must be non-passive so preventDefault() can suppress
    // native scroll while drag mode is active.
    el.addEventListener('pointermove', onPointerMove, { passive: false });
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerCancel);
    el.addEventListener('contextmenu', onContextMenu);
    el.addEventListener('dragstart', onDragStart);
    document.addEventListener('touchmove', onDocTouchMove, { passive: false });
    document.addEventListener('touchstart', onDocTouchStartTracker, { passive: true });
    document.addEventListener('touchend', onDocTouchEnd);
    document.addEventListener('touchcancel', onDocTouchEnd);

    return () => {
      clearLongPressTimer();
      stopAutoScroll();
      stopWatchdog();
      // Safety: if the hook unmounts mid-drag, restore any global styles
      // we may have changed.
      if (savedBodyOverscrollBehavior !== null) {
        document.body.style.overscrollBehavior = savedBodyOverscrollBehavior;
        savedBodyOverscrollBehavior = null;
      }
      if (savedHtmlTouchAction !== null) {
        document.documentElement.style.touchAction = savedHtmlTouchAction;
        savedHtmlTouchAction = null;
      }
      if (savedBodyTouchAction !== null) {
        document.body.style.touchAction = savedBodyTouchAction;
        savedBodyTouchAction = null;
      }
      if (scroller && savedScrollerTouchAction !== null) {
        scroller.style.touchAction = savedScrollerTouchAction;
        savedScrollerTouchAction = null;
      }
      restoreAncestorScrollables();
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerCancel);
      el.removeEventListener('contextmenu', onContextMenu);
      el.removeEventListener('dragstart', onDragStart);
      document.removeEventListener('touchmove', onDocTouchMove);
      document.removeEventListener('touchstart', onDocTouchStartTracker);
      document.removeEventListener('touchend', onDocTouchEnd);
      document.removeEventListener('touchcancel', onDocTouchEnd);
    };
  }, [disabled, containerRef]);
}
